/** SUNTRONIX ONE — Bank & Regulatory Details.
 *  Company-level Account Holder Name + MSME (company_profile) plus a manager for
 *  the multiple authorised bank accounts (bank_accounts collection) that print on
 *  quotations. Supports Add / Edit / Deactivate and a Bank Account Category master.
 */
import { useEffect, useState } from "react";
import { Landmark, Loader2, Save, ShieldCheck, Plus, Pencil, Check, X, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { BankQrPanel } from "@/components/bank/BankQrPanel";
import { IssuerUpiQrPanel } from "@/components/bank/IssuerUpiQrPanel";

function useCategories() {
  const [cats, setCats] = useState([]);
  const load = () => api.get("/bank-accounts/categories")
    .then(({ data }) => setCats(data.items || [])).catch(() => setCats([]));
  useEffect(() => { load(); }, []);
  return { cats, reload: load };
}

function CategoryManager({ cats, reload }) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const add = async () => {
    if (!adding.trim()) return;
    try { await api.post("/bank-accounts/categories", { name: adding.trim() });
      setAdding(""); toast.success("Category added"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };
  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    try { await api.patch(`/bank-accounts/categories/${id}`, { name: editName.trim() });
      setEditId(null); toast.success("Category updated"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  return (
    <div>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen((o) => !o)}
        data-testid="bank-category-manage"><Pencil className="h-3.5 w-3.5 mr-1" /> Add / Edit categories</Button>
      {open && (
        <div className="mt-2 border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2" data-testid="bank-category-manager">
          <div className="flex items-center gap-2">
            <Input value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="New category name"
              data-testid="bank-category-new" onKeyDown={(e) => e.key === "Enter" && add()} />
            <Button variant="create" type="button" size="sm" onClick={add} data-testid="bank-category-add" className="shrink-0"><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
          </div>
          <div className="divide-y divide-slate-200 max-h-48 overflow-y-auto">
            {cats.map((c) => (
              <div key={c.id} className="flex items-center gap-2 py-1.5">
                {editId === c.id ? (
                  <>
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8 flex-1"
                      data-testid={`bank-category-edit-input-${c.id}`} onKeyDown={(e) => e.key === "Enter" && saveEdit(c.id)} />
                    <button onClick={() => saveEdit(c.id)} data-testid={`bank-category-edit-save-${c.id}`} className="text-emerald-700 hover:bg-emerald-50 rounded p-1"><Check className="h-4 w-4" /></button>
                    <button onClick={() => setEditId(null)} className="text-slate-400 hover:bg-slate-100 rounded p-1"><X className="h-4 w-4" /></button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-[13px] text-slate-700">{c.name}{c.active === false && <span className="ml-2 text-[10px] text-slate-400">(inactive)</span>}</span>
                    <button onClick={() => { setEditId(c.id); setEditName(c.name); }} data-testid={`bank-category-edit-${c.id}`} className="text-indigo-600 hover:bg-indigo-50 rounded p-1"><Pencil className="h-3.5 w-3.5" /></button>
                  </>
                )}
              </div>))}
          </div>
        </div>)}
    </div>
  );
}

const BLANK = { label: "", currency: "INR", bank_name: "", account_number: "",
  account_holder_name: "", bank_account_category: "", ifsc: "", swift: "", micr: "",
  branch: "", branch_address: "", crn: "", quotation_enabled: true, status: "ACTIVE" };

function AccountModal({ editing, cats, onClose, onSaved }) {
  const [f, setF] = useState(editing ? { ...BLANK, ...editing } : { ...BLANK });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.label.trim() || !f.bank_name.trim() || !f.account_number.trim()) {
      toast.error("Label, Bank Name and Account # are required"); return;
    }
    setBusy(true);
    try {
      if (editing) await api.patch(`/bank-accounts/${editing.id}`, f);
      else await api.post("/bank-accounts", f);
      toast.success(editing ? "Bank account updated" : "Bank account added");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const Fld = ({ label, k, ph, mono = true }) => (
    <label className="block">
      <div className="text-[11px] font-medium text-slate-600 mb-1">{label}</div>
      <Input value={f[k]} onChange={(e) => u(k, e.target.value)} placeholder={ph}
        data-testid={`ba-${k}`} className={mono ? "font-mono" : ""} />
    </label>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="bank-account-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[88vh] flex flex-col">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <div className="text-[14px] font-heading font-semibold">{editing ? "Edit bank account" : "Add bank account"}</div>
          <div className="flex-1" />
          <button onClick={onClose} data-testid="ba-close" className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-4 py-3 overflow-y-auto grid grid-cols-2 gap-3">
          <Fld label="Label" k="label" ph="INR – AXIS BANK" mono={false} />
          <label className="block">
            <div className="text-[11px] font-medium text-slate-600 mb-1">Currency</div>
            <select value={f.currency} onChange={(e) => u("currency", e.target.value)} data-testid="ba-currency"
              className="h-9 w-full border border-slate-200 rounded px-2 text-[13px] bg-white">
              <option value="INR">INR</option><option value="USD">USD</option>
            </select>
          </label>
          <Fld label="Account Holder Name" k="account_holder_name" ph="As per bank records" />
          <label className="block">
            <div className="text-[11px] font-medium text-slate-600 mb-1">Bank Account Category</div>
            <select value={f.bank_account_category} onChange={(e) => u("bank_account_category", e.target.value)} data-testid="ba-category"
              className="h-9 w-full border border-slate-200 rounded px-2 text-[13px] bg-white">
              <option value="">— Select —</option>
              {cats.filter((c) => c.active !== false).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              {f.bank_account_category && !cats.some((c) => c.name === f.bank_account_category) && <option value={f.bank_account_category}>{f.bank_account_category}</option>}
            </select>
          </label>
          <Fld label="Bank Name" k="bank_name" ph="A X I S  B A N K" />
          <Fld label="Account #" k="account_number" ph="9 1 2 0 ..." />
          <Fld label="IFSC" k="ifsc" ph="U T I B 0 0 0 0 6 2 0" />
          <Fld label="SWIFT (USD)" k="swift" ph="A X I S I N B B 3 4 5" />
          <Fld label="MICR" k="micr" ph="6 0 0 2 1 1 0 1 9" />
          <Fld label="Branch" k="branch" ph="K I L P A U K" />
          <Fld label="Branch address (prints with bank details)" k="branch_address" ph="Full branch address" />
          <Fld label="CRN / customer reference (internal — never printed)" k="crn" ph="Bank customer reference" />
          <label className="col-span-2 flex items-center gap-2 text-[12px] text-slate-600 mt-1">
            <input type="checkbox" checked={f.quotation_enabled} data-testid="ba-quote-enabled"
              onChange={(e) => u("quotation_enabled", e.target.checked)} /> Show on quotations
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-100">
          <button onClick={onClose} className="text-[12px] px-3 py-1.5 rounded-md border border-slate-300">Cancel</button>
          <Button onClick={save} disabled={busy} className="bg-indigo-700" data-testid="ba-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function BankDetails() {
  const { user } = useAuth();
  const [owner, setOwner] = useState(null);
  const canEdit = ["super_admin", "management"].includes(user?.role) && !!owner;
  const { cats, reload: reloadCats } = useCategories();
  const [profile, setProfile] = useState(null);
  const [savingP, setSavingP] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [modal, setModal] = useState(null); // {editing} | 'new'

  const [loadErr, setLoadErr] = useState(null);
  const loadAccounts = () => api.get("/bank-accounts")
    .then(({ data }) => { setAccounts(data.items || []); setOwner(data.owner); setLoadErr(null); })
    .catch((e) => setLoadErr(e?.response?.status === 401 ? "Your session has expired — please sign in again to load bank details." : (e?.response?.data?.detail || "Could not load bank accounts.")));
  useEffect(() => {
    api.get("/bank-accounts/profile").then(({ data }) => setProfile({
      account_holder_name: data.account_holder_name || "", msme_no: data.msme_no || "" }))
      .catch(() => setProfile({ account_holder_name: "", msme_no: "" }));
    loadAccounts();
  }, []);

  const saveProfile = async () => {
    setSavingP(true);
    try { await api.put("/bank-accounts/profile", profile); toast.success(`Saved for ${owner?.name}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingP(false); }
  };
  const deactivate = async (a) => {
    if (!window.confirm(`Deactivate ${a.label}? It will stop appearing on quotations (history kept).`)) return;
    try { await api.delete(`/bank-accounts/${a.id}`); toast.success("Deactivated"); loadAccounts(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="bank-details-page">
      <div className="text-[11px] uppercase tracking-widest text-indigo-700 font-semibold">Configuration Studio · Commercial</div>
      <h1 className="text-2xl font-heading font-bold flex items-center gap-2"><Landmark className="h-6 w-6 text-indigo-600" /> Bank &amp; Regulatory Details</h1>
      <p className="text-sm text-slate-500 mt-1">These appear in the quotation "Bank &amp; Regulatory Information" box. Values print exactly as typed — keep any spacing you want to see on the PDF.</p>
      {!canEdit && <span className="mt-2 inline-flex text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Read only</span>}

      <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-[12px] font-semibold text-indigo-900" data-testid="bank-owner">
        {owner ? <>Issuer: {owner.name} — accounts and details below belong to this company only</> : loadErr ? <>Bank details not loaded</> : <>All Establishments (read-only group view) — select a company to edit its bank details</>}
      </div>
      {loadErr && <div className="mt-2 text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1" data-testid="bank-load-error">{loadErr}</div>}
      {!profile ? <Loader2 className="h-5 w-5 animate-spin text-slate-400 mt-8" /> : (
        <div className="mt-6 space-y-6">
          <div className="border border-slate-200 rounded-xl p-4 space-y-3">
            <div className="text-[13px] font-semibold text-slate-700">Company-level (prints on every document)</div>
            <label className="block">
              <div className="text-[12px] font-medium text-slate-600 mb-1">Account Holder Name</div>
              <Input value={profile.account_holder_name} disabled={!canEdit} data-testid="bank-account_holder_name"
                onChange={(e) => setProfile((s) => ({ ...s, account_holder_name: e.target.value }))} placeholder={owner?.name || ""} className="font-mono" />
            </label>
            <label className="block">
              <div className="text-[12px] font-medium text-slate-600 mb-1">MSME</div>
              <Input value={profile.msme_no} disabled={!canEdit} data-testid="bank-msme_no"
                onChange={(e) => setProfile((s) => ({ ...s, msme_no: e.target.value }))} placeholder="Udyam registration no. (optional)" className="font-mono" />
            </label>
            {canEdit && <div className="flex justify-end"><Button onClick={saveProfile} disabled={savingP} className="bg-indigo-700" data-testid="bank-save-btn">{savingP ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save</Button></div>}
          </div>

          <IssuerUpiQrPanel key={owner?.id || "none"} />

          <div className="border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <div className="text-[13px] font-semibold text-slate-700">Bank Accounts <span className="text-slate-400 font-normal">({accounts.length})</span></div>
              <div className="flex-1" />
              {canEdit && <CategoryManager cats={cats} reload={reloadCats} />}
              {canEdit && <Button variant="create" onClick={() => setModal("new")} className="" data-testid="bank-add-account"><Plus className="h-4 w-4 mr-1" /> Add Bank Account</Button>}
            </div>
            <div className="space-y-2">
              {accounts.length === 0 && <div className="text-[13px] text-slate-400 py-6 text-center" data-testid="bank-accounts-empty">No bank accounts yet — click "Add Bank Account".</div>}
              {accounts.map((a) => (
                <div key={a.id} className="border border-slate-200 rounded-lg p-3 flex items-start gap-3" data-testid={`bank-acct-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[13px]">{a.label || a.bank_name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{a.currency}</span>
                      {a.bank_account_category && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{a.bank_account_category}</span>}
                      {a.status === "INACTIVE" ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-600">Inactive</span>
                        : a.quotation_enabled === false ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">Hidden on quotes</span>
                        : <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">On quotes</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono mt-1 truncate">
                      {a.account_holder_name ? `${a.account_holder_name} · ` : ""}{a.bank_name} · A/C {a.account_number} {a.ifsc ? `· IFSC ${a.ifsc}` : ""}{a.swift ? `· SWIFT ${a.swift}` : ""}{a.branch ? `· ${a.branch}` : ""}
                    </div>
                    <BankQrPanel account={a} canEdit={canEdit} canVerify={canEdit} onChanged={loadAccounts} />
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => setModal({ editing: a })} data-testid={`bank-acct-edit-${a.id}`} className="text-indigo-600 hover:bg-indigo-50 rounded p-1.5"><Pencil className="h-4 w-4" /></button>
                      {a.status !== "INACTIVE" && <button onClick={() => deactivate(a)} data-testid={`bank-acct-del-${a.id}`} className="text-rose-500 hover:bg-rose-50 rounded p-1.5"><Trash2 className="h-4 w-4" /></button>}
                    </div>)}
                </div>))}
            </div>
          </div>
        </div>)}

      {modal && <AccountModal editing={modal === "new" ? null : modal.editing} cats={cats}
        onClose={() => setModal(null)} onSaved={loadAccounts} />}
    </div>
  );
}
